import { z } from 'zod';

/**
 * The life of one issued discount (`03-courses-discounts.md` §Data model).
 *
 * `granted → redeemed | expired | revoked`. All three exits are terminal, and
 * only one of them is the member's doing.
 *
 * **A grant is never deleted.** It is commercial value the platform handed out,
 * and the row is what a support conversation, an audit trail and a refund
 * argument are settled from — the same discipline the permission catalogue keeps
 * for orphaned keys and the template catalogue keeps for retired copy.
 */
export const DISCOUNT_GRANT_STATUSES = ['granted', 'redeemed', 'expired', 'revoked'] as const;

export const DiscountGrantStatusSchema = z.enum(DISCOUNT_GRANT_STATUSES);

export type DiscountGrantStatus = (typeof DISCOUNT_GRANT_STATUSES)[number];

const STATUS_SET: ReadonlySet<string> = new Set(DISCOUNT_GRANT_STATUSES);

export function isDiscountGrantStatus(value: string): value is DiscountGrantStatus {
  return STATUS_SET.has(value);
}

/**
 * Whether this grant may still reduce a price (matrix rows 14, 17).
 *
 * Expiry is **status**, not arithmetic on `expiresAt`: the sweep is what moves a
 * grant, so a reader comparing dates and a reader comparing statuses would
 * disagree for as long as a tick takes. The one place both are consulted is the
 * sweep's own claim, which is where the disagreement is resolved rather than
 * observed.
 */
export function isSpendableGrantStatus(status: DiscountGrantStatus): boolean {
  return status === 'granted';
}

/**
 * Whether a redemption may claim it.
 *
 * The same answer as {@link isSpendableGrantStatus}, and it exists as its own
 * name because the two questions are asked by different callers for different
 * reasons — the resolver asks what a member *has*, the redemption claim asks
 * what it may *take*. Keeping one derived from the other is what stops them
 * drifting the first time somebody adds a status.
 */
export function isRedeemableGrantStatus(status: DiscountGrantStatus): boolean {
  return isSpendableGrantStatus(status);
}
